// 管理者用：教室の休日設定ページ（/admin/schedule/holidays）

import React, { useEffect, useState } from "react";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import { formatDate } from "../../utils/formatDate";

const weekdayOptions = [
    { label: "日曜", value: 0 },
    { label: "月曜", value: 1 },
    { label: "火曜", value: 2 },
    { label: "水曜", value: 3 },
    { label: "木曜", value: 4 },
    { label: "金曜", value: 5 },
    { label: "土曜", value: 6 },
];

const periodOptions = [
    { label: "すべての講", value: "" },
    { label: "1講", value: 1 },
    { label: "2講", value: 2 },
    { label: "3講", value: 3 },
    { label: "4講", value: 4 },
    { label: "5講", value: 5 },
    { label: "6講", value: 6 },
    { label: "7講", value: 7 },
];

const AdminSchoolHolidays = () => {
    const [regulars, setRegulars] = useState([]);
    const [specials, setSpecials] = useState([]);
    const [formData, setFormData] = useState({
        type: "regular",
        id: null,
        day_of_week: "",
        period: "",
        date: "",
        start_date: "",
        end_date: "",
    });

    const fetchData = async () => {
        try {
            const res1 = await getRequest("schoolRegularHolidays");
            const res2 = await getRequest("schoolSpecialHolidays");
            setRegulars(res1.holidays);
            setSpecials(res2.holidays);
        } catch (err) {
            console.error("取得エラー", err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async () => {
        const { type, id, day_of_week, period, date, start_date, end_date } = formData;
        const isRegular = type === "regular";
        const sanitizeDate = (v) => v === "" ? null : v;

        const payload = isRegular
            ? {
                day_of_week: Number(day_of_week),
                period: period === "" ? null : Number(period),
                start_date: sanitizeDate(start_date),
                end_date: sanitizeDate(end_date)
            }
            : {
                date: sanitizeDate(date),
                period: period === "" ? null : Number(period)
            };
        const endpoint = isRegular ? "schoolRegularHolidays" : "schoolSpecialHolidays";

        if ((isRegular && (!day_of_week || !start_date)) || (!isRegular && !date)) {
            toast.error("必須項目が未入力です");
            return;
        }

        try {
            let res;
            if (id) {
                res = await putRequest(`${endpoint}/${id}`, payload);
            } else {
                res = await postRequest(endpoint, payload);
            }

            if (res.ok) {
                toast.success(id ? "更新しました" : "登録しました");
                setFormData({ type: "regular", id: null, day_of_week: "", period: "", date: "", start_date: "", end_date: "" });
                fetchData();
            } else {
                const data = await res.json();
                toast.error(data.error || "送信に失敗しました");
            }
        } catch (err) {
            console.error("送信エラー", err);
            toast.error("送信に失敗しました");
        }
    };

    const handleEdit = (item, type) => {
        setFormData({
            type,
            id: item.id,
            day_of_week: item.day_of_week ?? "",
            period: item.period ?? "",
            date: item.date ?? "",
            start_date: item.start_date ?? "",
            end_date: item.end_date ?? "",
        });
    };

    const handleDelete = async (id, type) => {
        const endpoint = type === "regular" ? "schoolRegularHolidays" : "schoolSpecialHolidays";
        if (!window.confirm("本当に削除しますか？")) return;
        try {
            const res = await deleteRequest(`${endpoint}/${id}`);
            if (res.ok) {
                toast.success("削除しました");
                fetchData();
            } else {
                const data = await res.json();
                toast.error(data.error || "削除に失敗しました");
            }
        } catch (err) {
            console.error("削除エラー", err);
            toast.error("削除に失敗しました");
        }
    };

    return (
        <div className="p-6 pt-20 bg-gray-100 min-h-screen">
            <h1 className="text-2xl font-bold mb-4">教室の休日設定</h1>

            <div className="bg-white p-4 rounded shadow mb-6">
                <h2 className="text-lg font-semibold mb-2">{formData.id ? "編集" : "新規追加"}</h2>
                <div className="flex gap-4 mb-3">
                    <label><input type="radio" name="type" value="regular" checked={formData.type === "regular"} onChange={handleChange} /> 毎週固定</label>
                    <label><input type="radio" name="type" value="special" checked={formData.type === "special"} onChange={handleChange} /> 特別休講</label>
                </div>

                {formData.type === "regular" ? (
                    <div className="grid grid-cols-2 gap-4">
                        <select name="day_of_week" value={formData.day_of_week} onChange={handleChange} className="p-2 border rounded">
                            <option value="">曜日を選択</option>
                            {weekdayOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <select name="period" value={formData.period} onChange={handleChange} className="p-2 border rounded">
                            {periodOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <div>
                            <label className="block text-sm font-medium mb-1">開始日</label>
                            <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} className="p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">終了日（任意）</label>
                            <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} className="p-2 border rounded w-full" />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">日付</label>
                            <input type="date" name="date" value={formData.date} onChange={handleChange} className="p-2 border rounded w-full" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">講</label>
                            <select name="period" value={formData.period} onChange={handleChange} className="p-2 border rounded w-full">
                                {periodOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                    </div>
                )}

                <button onClick={handleSubmit} className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                    {formData.id ? "更新" : "追加"}
                </button>
            </div>

            {/* 表示部はそのまま */}
            <div className="bg-white p-4 rounded shadow mb-6">
                <h2 className="text-lg font-semibold mb-2">毎週固定の休講</h2>
                <table className="w-full border">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="p-2 border">曜日</th>
                            <th className="p-2 border">講</th>
                            <th className="p-2 border">開始</th>
                            <th className="p-2 border">終了</th>
                            <th className="p-2 border">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {regulars.map(item => (
                            <tr key={item.id} className="border">
                                <td className="p-2 border">{item.day_of_week}</td>
                                <td className="p-2 border">{item.period ?? "すべて"}</td>
                                <td className="p-2 border">{formatDate(item.start_date)}</td>
                                <td className="p-2 border">{formatDate(item.end_date)}</td>
                                <td className="p-2 border flex gap-2">
                                    <button onClick={() => handleEdit(item, "regular")} className="text-blue-600 hover:text-blue-800"><FaEdit /></button>
                                    <button onClick={() => handleDelete(item.id, "regular")} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white p-4 rounded shadow">
                <h2 className="text-lg font-semibold mb-2">特別休講日</h2>
                <table className="w-full border">
                    <thead>
                        <tr className="bg-gray-200">
                            <th className="p-2 border">日付</th>
                            <th className="p-2 border">講</th>
                            <th className="p-2 border">操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {specials.map(item => (
                            <tr key={item.id} className="border">
                                <td className="p-2 border">{formatDate(item.date)}</td>
                                <td className="p-2 border">{item.period ?? "すべて"}</td>
                                <td className="p-2 border flex gap-2">
                                    <button onClick={() => handleEdit(item, "special")} className="text-blue-600 hover:text-blue-800"><FaEdit /></button>
                                    <button onClick={() => handleDelete(item.id, "special")} className="text-red-600 hover:text-red-800"><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminSchoolHolidays;
